import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth.tsx';
import {
  getSubmission,
  listSubmissionPages,
  listSubmissionsWithTakeoffData,
  listTakeoffSessions,
} from '../lib/db.ts';
import type { Submission, SubmissionPage } from '../lib/types.ts';
import {
  COLORS,
  Card,
  ErrorBox,
  Loader,
  mono,
} from '../components/takeoff/primitives.tsx';
import { OutputPreview } from '../components/output/OutputPreview.tsx';
import { GradeCard } from '../components/output/GradeCard.tsx';
import {
  buildCivilReport,
  buildRebarReport,
  pickSession,
  type OutputData,
} from '../components/output/helpers.ts';
import { downloadCsv } from '../components/output/exportCsv.ts';
import { downloadPdf } from '../components/output/exportPdf.ts';
import { gradeJob } from '../lib/grading.ts';

export function Output() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <OutputPicker />;
  return <OutputJob submissionId={id} />;
}

// ─── Picker (sidebar entry, /app/output) ─────────────────────────────────────

function OutputPicker() {
  const [rows, setRows] = useState<readonly Submission[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const data = await listSubmissionsWithTakeoffData();
        if (active) setRows(data);
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Load failed');
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (error) return <ErrorBox message={error} />;
  if (rows === null) return <Loader text="Loading jobs…" />;

  return (
    <div style={{ maxWidth: 720 }}>
      <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 28, fontWeight: 800, marginBottom: 8 }}>
        Output
      </h1>
      <p style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 18, lineHeight: 1.5 }}>
        Pick a job to render its consolidated estimate output. The renderer reads
        live takeoff data and produces an HTML preview plus PDF and CSV downloads.
      </p>

      {rows.length === 0 ? (
        <Card>
          <div style={{ fontSize: 13, color: COLORS.textDim }}>
            No jobs with takeoff data yet. Open a structural or civil takeoff session,
            add measurements, then return here to render output.
          </div>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((row) => (
            <Link
              key={row.id}
              to={`/app/jobs/${row.id}/output`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 14px',
                background: COLORS.bg,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 8,
                textDecoration: 'none',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ color: COLORS.text, fontWeight: 600, fontSize: 13 }}>
                  {row.job_name}
                </span>
                <span style={mono(10, COLORS.textDim)}>{row.source_pdf}</span>
              </div>
              <span style={mono(10, COLORS.green)}>RENDER →</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Job-scoped output (/app/jobs/:id/output) ────────────────────────────────

function OutputJob({ submissionId }: { submissionId: string }) {
  const { user } = useAuth();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [pages, setPages] = useState<readonly SubmissionPage[]>([]);
  const [civilSession, setCivilSession] = useState<
    Awaited<ReturnType<typeof listTakeoffSessions>>[number] | null
  >(null);
  const [rebarSession, setRebarSession] = useState<
    Awaited<ReturnType<typeof listTakeoffSessions>>[number] | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const [sub, pgs, sessions] = await Promise.all([
          getSubmission(submissionId),
          listSubmissionPages(submissionId),
          listTakeoffSessions(submissionId),
        ]);
        if (!active) return;
        if (!sub) {
          setError('Submission not found.');
          return;
        }
        setSubmission(sub);
        setPages(pgs);
        setCivilSession(pickSession(sessions, 'civil'));
        setRebarSession(pickSession(sessions, 'structural'));
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Load failed');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [submissionId]);

  const data = useMemo<OutputData | null>(() => {
    if (!submission) return null;
    return {
      submission,
      pages,
      civil: buildCivilReport(civilSession, pages),
      rebar: buildRebarReport(rebarSession),
      generatedAt: new Date(),
      preparedBy: user?.email ?? 'unknown',
    };
  }, [submission, pages, civilSession, rebarSession, user?.email]);

  const grade = useMemo(() => (submission ? gradeJob(submission, pages) : null), [submission, pages]);

  if (loading) return <Loader text="Loading output…" />;
  if (error) return <ErrorBox message={error} />;
  if (!submission || !data) return <Loader text="Loading…" />;

  const hasAny = (data.civil && data.civil.itemRows.length > 0) || (data.rebar && data.rebar.lineCount > 0);

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Link
          to={`/app/jobs/${submissionId}`}
          style={{ ...mono(12, COLORS.textDim), textDecoration: 'none' }}
        >
          ← {submission.job_name}
        </Link>
        <div style={{ flex: 1 }} />
        <ExportButton
          label="Download CSV"
          onClick={() => downloadCsv(data)}
          disabled={!hasAny}
          accent={COLORS.purple}
          border={COLORS.accentBorder}
        />
        <ExportButton
          label="Download PDF"
          onClick={() => downloadPdf(data)}
          disabled={!hasAny}
          accent={COLORS.green}
          border={COLORS.greenBorder}
        />
      </div>

      <GradeCard jobId={submissionId} grade={grade} />

      {!hasAny ? (
        <Card>
          <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.5 }}>
            No takeoff data yet for this job. Open a{' '}
            <Link to={`/app/jobs/${submissionId}/takeoff/rebar`} style={{ color: COLORS.green }}>
              structural
            </Link>{' '}
            or{' '}
            <Link to={`/app/jobs/${submissionId}/takeoff/civil`} style={{ color: COLORS.purple }}>
              civil
            </Link>{' '}
            takeoff and add measurements, then return to render the output.
          </div>
        </Card>
      ) : (
        <OutputPreview data={data} />
      )}
    </div>
  );
}

function ExportButton({
  label,
  onClick,
  disabled,
  accent,
  border,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  accent: string;
  border: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'transparent',
        border: `1px solid ${disabled ? COLORS.border : border}`,
        color: disabled ? COLORS.textDim : accent,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 11,
        letterSpacing: '0.1em',
        padding: '8px 14px',
        borderRadius: 5,
        textTransform: 'uppercase',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}
